import type { JSX } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { ScheduleWindow } from '@apiary/shared'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { TextField } from '@/ui/primitives/Field'
import {
  WEEK_DAYS,
  crossesMidnight,
  newScheduleWindow,
  toggleDay,
  type WindowProblems
} from './behaviours-model'
import { ToggleButton } from './behaviours-toggle'

/**
 * Редактор окон расписания: добавить, удалить, задать дни и время.
 *
 * До переписывания фронта окна показывались чипами только на чтение — задать их
 * было негде вообще, и включённое расписание превращало бота в неподнимаемого
 * (пустой список окон рантайм трактует как «никогда», docs/07 §6.5).
 *
 * Дни недели — не тумблеры, а группа кнопок-переключателей: семь тумблеров на
 * окно занимают полэкрана и читаются хуже, чем строка «пн вт ср чт пт сб вс».
 * Каждая кнопка несёт `aria-pressed` и полное название дня как доступное имя —
 * «пн» вслух не расшифровывается.
 */

export interface ScheduleEditorProps {
  windows: ScheduleWindow[]
  problems: WindowProblems[]
  disabled: boolean
  onChange: (windows: ScheduleWindow[]) => void
}

export function ScheduleEditor({
  windows,
  problems,
  disabled,
  onChange
}: ScheduleEditorProps): JSX.Element {
  const replace = (index: number, next: ScheduleWindow): void =>
    onChange(windows.map((item, at) => (at === index ? next : item)))

  return (
    <div className="flex flex-col gap-3">
      {windows.length === 0 ? (
        <p className="max-w-prose text-2xs text-ink-muted">
          Окон нет. Пока их нет, расписание держать включённым нельзя: бот не запустится ни в одно
          время.
        </p>
      ) : null}

      {windows.map((window, index) => (
        <WindowRow
          key={index}
          index={index}
          window={window}
          problems={problems[index] ?? { days: null, from: null, to: null }}
          disabled={disabled}
          onChange={next => replace(index, next)}
          onRemove={() => onChange(windows.filter((_unused, at) => at !== index))}
        />
      ))}

      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        disabled={disabled}
        onClick={() => onChange([...windows, newScheduleWindow()])}
      >
        <Plus aria-hidden className="size-4" />
        Добавить окно
      </Button>
    </div>
  )
}

function WindowRow({
  index,
  window,
  problems,
  disabled,
  onChange,
  onRemove
}: {
  index: number
  window: ScheduleWindow
  problems: WindowProblems
  disabled: boolean
  onChange: (next: ScheduleWindow) => void
  onRemove: () => void
}): JSX.Element {
  const groupLabel = `Дни недели, окно ${index + 1}`

  return (
    <div className="flex flex-col gap-2.5 rounded-sm border border-line bg-panel-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-xs font-medium text-ink-muted">{groupLabel}</span>
          <div role="group" aria-label={groupLabel} className="flex flex-wrap gap-1">
            {WEEK_DAYS.map(day => (
              <ToggleButton
                key={day.value}
                pressed={window.days.includes(day.value)}
                name={day.full}
                showCheck={false}
                disabled={disabled}
                className="min-w-9 justify-center"
                onPressedChange={on =>
                  onChange({ ...window, days: toggleDay(window.days, day.value, on) })
                }
              >
                {day.short}
              </ToggleButton>
            ))}
          </div>
          {problems.days === null ? null : (
            <p role="alert" className="text-2xs text-fail">
              {problems.days}
            </p>
          )}
        </div>

        <IconButton
          label={`Удалить окно ${index + 1}`}
          tone="danger"
          size="sm"
          disabled={disabled}
          onClick={onRemove}
        >
          <Trash2 aria-hidden className="size-4" />
        </IconButton>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <TextField
          label="Начало"
          value={window.from}
          error={problems.from}
          mono
          inputMode="numeric"
          maxLength={5}
          placeholder="18:00"
          disabled={disabled}
          className="w-24"
          onChange={event => onChange({ ...window, from: event.target.value })}
        />
        <TextField
          label="Конец"
          value={window.to}
          error={problems.to}
          mono
          inputMode="numeric"
          maxLength={5}
          placeholder="23:00"
          disabled={disabled}
          className="w-24"
          onChange={event => onChange({ ...window, to: event.target.value })}
        />
        {crossesMidnight(window) ? (
          <div className="flex h-16 items-center">
            <Chip tone="honey">через полночь</Chip>
          </div>
        ) : null}
      </div>
    </div>
  )
}
